import {
  CantonalBallot,
  CantonId,
  docToBallotState,
  emptyMultiLocaleString,
  FederalBallotSummary,
  mkCantonId,
  mkMunicipalityIdFromString,
  toFederalBallotSummary,
} from '@/domain'
import * as t from 'io-ts'
import * as React from 'react'
import {
  BallotDocumentBase,
  CantonalBallotDocument,
  FederalBallotDocument,
  PopularVoteDocument,
  ElectionDocument,
} from '../domain/documents'
import { useEnv } from './env'
import * as firestore from 'firebase/firestore'
import * as F from 'fp-ts'

export function isEqual(a: Request, b: Request) {
  if ('isEqual' in (a as any) && 'isEqual' in (b as any)) {
    return (a as any).isEqual(b as any)
  } else if ((a as any).type === 'query' && (b as any).type === 'query') {
    return firestore.queryEqual(a as any, b as any)
  } else if ((a as any).type === (b as any).type) {
    return firestore.refEqual(a as any, b as any)
  } else {
    return false
  }
}

const listenOptions: firestore.SnapshotListenOptions = {
  includeMetadataChanges: true,
}

type Request = firestore.DocumentReference | firestore.Query

type Snapshot<T> = T extends firestore.DocumentReference
  ? firestore.DocumentSnapshot
  : T extends firestore.Query
    ? firestore.QuerySnapshot
    : never

const snapshotCache = new Map<Request, Snapshot<Request>>()

export const useFirestoreSnapshot = <T extends Request>(req0?: T): undefined | Snapshot<T> => {
  const req = (() => {
    if (!req0) return undefined

    for (const k of snapshotCache.keys()) {
      if (safeGetPrototype(k) === safeGetPrototype(req0) && isEqual(k as any, req0 as any)) {
        return k
      }
    }

    return req0
  })()

  const [snapshot, setSnapshot] = React.useState(req ? snapshotCache.get(req) : undefined)
  if (req && snapshot !== snapshotCache.get(req)) {
    setSnapshot(snapshotCache.get(req))
  }

  React.useEffect(() => {
    if (!req) return

    return firestore.onSnapshot(req as any, listenOptions, (snapshot: any) => {
      if (req) {
        snapshotCache.set(req, snapshot)
        setSnapshot(snapshot)
      }
    })
  }, [req, setSnapshot])

  return snapshot as Snapshot<T>
}

export function useFirestoreDocument<T, REF extends firestore.DocumentReference>(
  mkRef: (db: firestore.Firestore) => REF,
  decoder: t.Decoder<unknown, T>,
): { ref: REF; data: undefined | T } {
  const { firebaseApp } = useEnv()
  const ref = mkRef(firestore.getFirestore(firebaseApp))
  const snapshot = useFirestoreSnapshot(ref)

  return React.useMemo(
    () => ({
      ref,
      data: snapshot
        ? F.function.pipe(
            decoder.decode(snapshot.data()),
            F.either.fold(
              (err) => {
                console.log(err)
                return undefined
              },
              (x) => x,
            ),
          )
        : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [decoder, snapshot],
  )
}

export function useFirestoreQuery<T, REF extends firestore.Query>(
  mkQuery: (db: firestore.Firestore) => REF,
  decoder: t.Decoder<unknown, T>,
): { query: REF; docs: undefined | { id: string; data: T }[] } {
  const { firebaseApp } = useEnv()
  const query = mkQuery(firestore.getFirestore(firebaseApp))
  const snapshot = useFirestoreSnapshot(query)

  return {
    query,
    docs: (() => {
      if (snapshot) {
        const docs: { id: string; data: T }[] = []

        for (const docSnapshot of snapshot.docs) {
          const doc = F.function.pipe(
            decoder.decode(docSnapshot.data()),
            F.either.fold(
              (err) => {
                console.log(err)
                return undefined
              },
              (x) => x,
            ),
          )

          if (doc) {
            docs.push({ id: docSnapshot.id, data: doc })
          } else {
            return undefined
          }
        }

        return docs
      } else {
        return undefined
      }
    })(),
  }
}

export const useElectionDocument = ({ year }: { year: string }) => {
  const doc = useFirestoreDocument((db) => firestore.doc(db, 'elections', year), ElectionDocument)
  return doc ? doc.data : undefined
}
export const usePopularVoteDocument = ({ popularVoteId }: { popularVoteId: string }) => {
  const doc = useFirestoreDocument((db) => firestore.doc(db, 'popularVotes', popularVoteId), PopularVoteDocument)
  return doc ? doc.data : undefined
}

export const useBallotDocument = (mkRef: (db: firestore.Firestore) => firestore.DocumentReference) => {
  const doc = useFirestoreDocument(mkRef, BallotDocumentBase)
  return doc ? doc.data : undefined
}

export const useFederalBallotsCollection = ({ popularVoteId }: { popularVoteId: string }) => {
  return useFirestoreQuery(
    (db) =>
      firestore.query(
        firestore.collection(db, 'popularVotes', popularVoteId, 'federalBallots'),
        firestore.orderBy('position'),
      ),
    FederalBallotDocument,
  ).docs
}

export const useFederalBallotDocument = ({ popularVoteId, ballotId }: { popularVoteId: string; ballotId: string }) => {
  const doc = useFirestoreDocument(
    (db) => firestore.doc(db, 'popularVotes', popularVoteId, 'federalBallots', ballotId),
    FederalBallotDocument,
  )
  return doc ? doc.data : undefined
}

export const useCantonalBallotsCollection = ({
  popularVoteId,
  cantonId,
}: {
  popularVoteId: string
  cantonId: string
}) => {
  return useFirestoreQuery(
    (db) =>
      firestore.query(
        firestore.collection(db, 'popularVotes', popularVoteId, 'cantons', cantonId, 'ballots'),
        firestore.orderBy('position'),
      ),
    CantonalBallotDocument,
  ).docs
}

export const useCantonalBallotDocument = ({
  popularVoteId,
  cantonId,
  ballotId,
}: {
  popularVoteId: string
  cantonId: string
  ballotId: string
}) => {
  const doc = useFirestoreDocument(
    (db) => firestore.doc(db, 'popularVotes', popularVoteId, 'cantons', cantonId, 'ballots', ballotId),
    CantonalBallotDocument,
  )
  return doc ? doc.data : undefined
}

/**
 * Returns true if the tobi integration should be enabled.
 */
export const useTobiIntegration = ({ popularVoteId }: { popularVoteId: string }): undefined | TobiIntegration => {
  const doc = usePopularVoteDocument({ popularVoteId })
  if (doc && doc.tobiIntegration) {
    return {
      enabled: !!doc.tobiIntegration.enabled,
      articleUrlTemplate: doc.tobiIntegration.articleUrlTemplate || '',
    }
  } else {
    return undefined
  }
}

export interface TobiIntegration {
  enabled: boolean
  articleUrlTemplate: string
}

export function useFederalBallotSummary(
  popularVoteId: string,
  federalBallotId: string,
): undefined | FederalBallotSummary {
  const { db } = useEnv()

  const popularVoteDocument = usePopularVoteDocument({ popularVoteId })
  const federalBallotsCollection = useFederalBallotsCollection({ popularVoteId })
  const cantons = useFirestoreSnapshot(
    firestore.query(
      firestore.collection(db, 'popularVotes', popularVoteId, 'federalBallots', federalBallotId, 'cantons'),
    ),
  )

  if (!popularVoteDocument || !federalBallotsCollection || !cantons) {
    return undefined
  }

  const federalBallot = federalBallotsCollection.find((x) => x.id === federalBallotId)
  if (!federalBallot) {
    return undefined
  }

  return toFederalBallotSummary({
    date: new Date(Date.parse(popularVoteId)),
    visibility: popularVoteDocument.visibility,
    docSnapshot: {
      ...federalBallot,
      data() {
        return federalBallot.data
      },
    } as any,
    cantonsSnapshot: cantons,
  })
}

export function useCantonalBallotSummary(
  popularVoteId: string,
  cantonId: CantonId,
  ballotId: string,
): undefined | CantonalBallot {
  const { db } = useEnv()

  const { canton, cantonalBallotDocument, municipalities } = (function useSilence() {
    const popularVote = firestore.doc(db, 'popularVotes', popularVoteId)
    const canton = firestore.doc(popularVote, 'cantons', cantonId)
    const ballot = firestore.doc(canton, 'ballots', ballotId)
    const municipalities = firestore.collection(ballot, 'municipalities')

    return {
      canton: useFirestoreSnapshot(canton),
      cantonalBallotDocument: useCantonalBallotDocument({ popularVoteId, cantonId, ballotId }),
      municipalities: useFirestoreSnapshot(municipalities),
    }
  })()

  const municipalityData = React.useMemo(
    () =>
      municipalities
        ? municipalities.docs.map((doc) => ({
            id: mkMunicipalityIdFromString(doc.id),
            name: doc.data().name,
            ballotState: docToBallotState(doc.data()),
          }))
        : [],
    [municipalities],
  )

  if (!canton || !cantonalBallotDocument || !municipalities) {
    return undefined
  }

  const ballotState = docToBallotState(cantonalBallotDocument)

  const cantonalBallot: CantonalBallot = {
    cantonId: mkCantonId(canton.id),

    name: cantonalBallotDocument.name,
    lead: cantonalBallotDocument.lead,

    detailName: cantonalBallotDocument.detailName || emptyMultiLocaleString,

    articleIds: cantonalBallotDocument.articleIds,

    ballotState,

    municipalities: municipalityData,
  }

  return cantonalBallot
}

// Example of a safe pattern to prevent this error
const safeGetPrototype = (obj: any) => {
  if (obj === null || obj === undefined) {
    return {}
  }
  return Object.getPrototypeOf(obj)
}
